import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * O PLAYER da conversa.
 *
 * Existe porque avaliar uma conversa sem poder ouvi-la é pedir para a pessoa
 * confiar na transcrição. Ouvir é o que fecha o ciclo: a evidência do score
 * aponta um minuto, e o minuto toca.
 *
 * Duas decisões que valem mais que o código:
 *
 * 1. **O áudio sai de uma rota com sessão**, e não de um endereço público. Por
 *    isso não há nada aqui que guarde ou monte credencial: o navegador manda o
 *    cookie, como em qualquer outra tela.
 * 2. **O tempo é publicado para fora.** Quem desenha a transcrição precisa
 *    saber que trecho está tocando, e quem toca precisa aceitar um pedido de
 *    ir para um minuto. É esse par que liga o áudio ao texto.
 */
import * as React from "react";
import { cn } from "../lib/cn.js";
const VELOCIDADES = [1, 1.25, 1.5, 2];
const relogio = (segundos) => {
    if (!Number.isFinite(segundos) || segundos < 0)
        return "0:00";
    const m = Math.floor(segundos / 60);
    const s = Math.floor(segundos % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
};
export const Player = React.forwardRef(function Player({ src, duracaoS, aoTempo, rotulo = "Gravação da conversa", video = false, className }, ref) {
    // `HTMLMediaElement` e não `HTMLAudioElement`: os controles são os mesmos
    // para os dois, e é isso que deixa o player servir áudio e vídeo sem virar
    // dois componentes que envelhecem separados.
    const audio = React.useRef(null);
    const [tocando, setTocando] = React.useState(false);
    const [agora, setAgora] = React.useState(0);
    const [total, setTotal] = React.useState(duracaoS ?? 0);
    const [velocidade, setVelocidade] = React.useState(1);
    const [erro, setErro] = React.useState(null);
    React.useImperativeHandle(ref, () => ({
        irPara(ms) {
            const el = audio.current;
            if (!el)
                return;
            el.currentTime = Math.max(0, ms / 1000);
            setAgora(el.currentTime);
            // Clicar num trecho é pedido para OUVIR aquele trecho. Só mover a agulha
            // e ficar em silêncio faz a pessoa clicar de novo achando que não pegou.
            void el.play().then(() => setTocando(true)).catch(() => undefined);
        },
        tocar() { void audio.current?.play(); },
        pausar() { audio.current?.pause(); },
    }), []);
    const alternar = () => {
        const el = audio.current;
        if (!el)
            return;
        if (el.paused)
            void el.play().catch(() => setErro("Não consegui tocar a gravação agora."));
        else
            el.pause();
    };
    const trocarVelocidade = () => {
        const proxima = VELOCIDADES[(VELOCIDADES.indexOf(velocidade) + 1) % VELOCIDADES.length];
        setVelocidade(proxima);
        if (audio.current)
            audio.current.playbackRate = proxima;
    };
    const arrastar = (e) => {
        const el = audio.current;
        if (!el)
            return;
        el.currentTime = Number(e.target.value);
        setAgora(el.currentTime);
    };
    return (_jsxs("div", { className: cn("enotes-player", className), "data-erro": erro ? "sim" : undefined, children: [React.createElement(video ? "video" : "audio", {
                ref: audio,
                src,
                preload: "metadata",
                ...(video ? { playsInline: true, className: "enotes-player-tela" } : {}),
                onLoadedMetadata: (e) => {
                    const d = e.currentTarget.duration;
                    if (Number.isFinite(d) && d > 0)
                        setTotal(d);
                },
                onTimeUpdate: (e) => {
                    const t = e.currentTarget.currentTime;
                    setAgora(t);
                    aoTempo?.(Math.round(t * 1000));
                },
                onPlay: () => { setTocando(true); setErro(null); },
                onPause: () => setTocando(false),
                onEnded: () => setTocando(false),
                onError: () => setErro("A gravação não está disponível."),
            }), _jsx("button", { type: "button", className: "enotes-player-botao", onClick: alternar, "aria-label": tocando ? `Pausar ${rotulo}` : `Tocar ${rotulo}`, disabled: !!erro, children: tocando ? _jsx(IconePausa, {}) : _jsx(IconeTocar, {}) }), _jsx("span", { className: "carimbo num enotes-player-tempo", children: relogio(agora) }), _jsx("input", { type: "range", className: "enotes-player-barra", min: 0, max: Math.max(1, total), step: 0.1, value: Math.min(agora, total || 1), onChange: arrastar, "aria-label": `Posição em ${rotulo}`, "aria-valuetext": relogio(agora), disabled: !!erro }), _jsx("span", { className: "carimbo num enotes-player-tempo", children: relogio(total) }), _jsxs("button", { type: "button", className: "enotes-player-velocidade", onClick: trocarVelocidade, "aria-label": `Velocidade da reprodução: ${velocidade} vezes. Clique para mudar.`, disabled: !!erro, children: [velocidade, "x"] }), erro && _jsx("span", { className: "enotes-player-erro", children: erro })] }));
});
const IconeTocar = () => (_jsx("svg", { viewBox: "0 0 24 24", width: "16", height: "16", "aria-hidden": true, focusable: "false", children: _jsx("path", { d: "M8 5.5v13l11-6.5z", fill: "currentColor" }) }));
const IconePausa = () => (_jsx("svg", { viewBox: "0 0 24 24", width: "16", height: "16", "aria-hidden": true, focusable: "false", children: _jsx("path", { d: "M7 5h3.5v14H7zM13.5 5H17v14h-3.5z", fill: "currentColor" }) }));
//# sourceMappingURL=midia.js.map